// components/SubscriptionModal.js
import React, { useState, useEffect } from 'react';
import { auth, database } from '../firebase/firebaseConfig';
import { getAuthHeaders } from '../utils/auth-helpers';

const SubscriptionModal = ({ isOpen, onClose, userSubscription }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    if (!isOpen) return null;

    const daysRemaining = () => {
        if (!userSubscription?.planEndDate) return 0;
        const endDate = new Date(userSubscription.planEndDate);
        const now = new Date();
        return Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)));
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // In SubscriptionModal.js, update the handleCancelSubscription function:
    // In SubscriptionModal.js, update handleCancelSubscription with better error handling:

    // In SubscriptionModal.js, update the handleCancelSubscription function:

    const handleCancelSubscription = async () => {
        const confirmMessage = userSubscription?.status === 'cancelled'
            ? 'Your subscription is already cancelled. Do you want to remove the subscription data?'
            : `Are you sure you want to cancel your subscription? You will retain access until ${formatDate(userSubscription.planEndDate)} but will not be charged for the next month.`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        setLoading(true);
        setError('');

        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('No authenticated user');
            }

            // If subscription is already cancelled, just update UI
            if (userSubscription?.status === 'cancelled') {
                setSuccessMessage('Your subscription is already cancelled.');
                setLoading(false);
                return;
            }

            console.log('Attempting to cancel subscription...');
            console.log('User ID:', user.uid);
            console.log('Subscription ID:', userSubscription?.subscriptionId);

            // Get auth headers
            const headers = await getAuthHeaders();
            console.log('Headers prepared:', headers);
            const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
            // Call backend to cancel Stripe subscription
            const apiUrl = `${API_BASE_URL}/api/v1/cancel-subscription`;
            console.log('Calling API:', apiUrl);

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({
                    // Include subscription ID explicitly if needed
                    subscriptionId: userSubscription?.subscriptionId
                })
            });

            console.log('Response status:', response.status);
            console.log('Response ok:', response.ok);

            // Check if response is JSON
            const contentType = response.headers.get("content-type");
            console.log('Response content-type:', contentType);

            let data;
            if (contentType && contentType.includes("application/json")) {
                data = await response.json();
                // LOG THE ACTUAL ERROR DETAILS
                console.log('Response data:', JSON.stringify(data, null, 2));
            } else {
                // Response is not JSON (might be HTML error page)
                const text = await response.text();
                console.error('Non-JSON response:', text);

                // Check for common errors
                if (text.includes('Cannot POST')) {
                    throw new Error('API endpoint not found. Please check the server is running and the endpoint exists.');
                } else if (text.includes('<!DOCTYPE')) {
                    throw new Error('Server returned an HTML error page. Check server logs.');
                } else {
                    throw new Error('Server returned invalid response: ' + text.substring(0, 100));
                }
            }

            if (!response.ok) {
                // LOG THE FULL ERROR OBJECT
                console.error('API Error Response:', JSON.stringify(data, null, 2));
                console.error('Error message:', data?.error);
                console.error('Error type:', data?.type);
                console.error('Full error details:', data?.message);

                // Handle specific error cases
                if (response.status === 404) {
                    if (data?.requiresSupport) {
                        throw new Error('Subscription not found in payment system. Please contact support.');
                    } else {
                        throw new Error('API endpoint not found. Please check server configuration.');
                    }
                } else if (response.status === 401) {
                    throw new Error('Authentication failed. Please log in again.');
                } else if (response.status === 403) {
                    throw new Error('You do not have permission to cancel this subscription.');
                } else if (response.status === 500) {
                    // Server error - show the actual error message
                    console.error('Server error details:', data);
                    throw new Error(data?.error || data?.message || 'Server error. Please check server logs for details.');
                } else {
                    throw new Error(data?.error || `Failed to cancel subscription (Error ${response.status})`);
                }
            }

            console.log('Subscription cancelled successfully:', data);

            // Show success message with details
            setSuccessMessage(
                `Your subscription has been cancelled successfully. ` +
                `You will retain full access until ${formatDate(data.details?.accessUntil || userSubscription.planEndDate)}. ` +
                `You will not be charged again.`
            );

            // Update local state to reflect cancellation
            if (userSubscription) {
                userSubscription.status = 'cancelled';
                userSubscription.cancelledAt = data.details?.cancelledAt || new Date().toISOString();
            }

            // Refresh the page after 3 seconds to update all components
            setTimeout(() => {
                window.location.reload();
            }, 3000);

        } catch (err) {
            console.error('Cancellation error details:', {
                message: err.message,
                stack: err.stack,
                userSubscription: userSubscription
            });

            setError(err.message || 'Failed to cancel subscription. Please try again or contact support.');
        } finally {
            setLoading(false);
        }
    };

    const handleReactivateSubscription = async () => {
        if (!window.confirm('Do you want to reactivate your subscription? You will be charged again starting from the next billing cycle.')) {
            return;
        }

        setLoading(true);
        setError('');
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${API_BASE_URL}/api/v1/reactivate-subscription`, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({
                    subscriptionId: userSubscription.subscriptionId,
                    firebaseId: auth.currentUser.uid
                })
            });

            if (!response.ok) {
                throw new Error('Failed to reactivate subscription');
            }

            const data = await response.json();
            console.log('Subscription reactivated:', data);

            // Update Firebase
            const userRef = database.ref(`users/${auth.currentUser.uid}`);
            await userRef.update({
                'subscription/status': 'active',
                'subscription/cancelAtPeriodEnd': false,
                'subscription/reactivatedAt': new Date().toISOString()
            });

            setSuccessMessage('Your subscription has been reactivated successfully!');

            setTimeout(() => {
                window.location.reload();
            }, 2000);

        } catch (err) {
            setError('Failed to reactivate subscription. Please contact support.');
            console.error('Reactivation error:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {/* Modal Backdrop */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                {/* Modal Content */}
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: '32px',
                        maxWidth: '500px',
                        width: '90%',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }}
                >
                    {/* Header */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '24px'
                    }}>
                        <h2 style={{
                            fontSize: '24px',
                            fontWeight: '600',
                            color: '#1f2937'
                        }}>
                            Subscription Management
                        </h2>
                        <button
                            onClick={onClose}
                            style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '24px',
                                color: '#6b7280',
                                cursor: 'pointer',
                                padding: '4px'
                            }}
                        >
                            ×
                        </button>
                    </div>

                    {/* Success Message */}
                    {successMessage && (
                        <div style={{
                            backgroundColor: '#d1fae5',
                            border: '1px solid #6ee7b7',
                            color: '#065f46',
                            padding: '12px',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            fontSize: '14px'
                        }}>
                            {successMessage}
                        </div>
                    )}

                    {/* Error Message */}
                    {error && (
                        <div style={{
                            backgroundColor: '#fee2e2',
                            border: '1px solid #fecaca',
                            color: '#dc2626',
                            padding: '12px',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            fontSize: '14px'
                        }}>
                            {error}
                        </div>
                    )}

                    {/* Subscription Details */}
                    <div style={{
                        backgroundColor: '#f9fafb',
                        borderRadius: '8px',
                        padding: '20px',
                        marginBottom: '24px'
                    }}>
                        <h3 style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#1f2937',
                            marginBottom: '16px'
                        }}>
                            Current Subscription
                        </h3>

                        <div style={{ fontSize: '14px', color: '#6b7280' }}>
                            <div style={{ marginBottom: '12px' }}>
                                <strong>Plan:</strong> RAGRecruit Legal Pro (Monthly)
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                                <strong>Status:</strong>{' '}
                                <span style={{
                                    color: userSubscription?.status === 'active' ? '#10b981' :
                                        userSubscription?.status === 'cancelled' ? '#f59e0b' : '#ef4444',
                                    fontWeight: '600'
                                }}>
                                    {userSubscription?.status === 'active' ? 'Active' :
                                        userSubscription?.status === 'cancelled' ? 'Cancelled (Active until end of period)' : 'Inactive'}
                                </span>
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                                <strong>Billing:</strong> ${userSubscription?.amount || process.env.REACT_APP_PAYMENT} {(userSubscription?.currency || 'CAD').toUpperCase()}/month
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                                <strong>Current Period:</strong> {formatDate(userSubscription?.planStartDate)} - {formatDate(userSubscription?.planEndDate)}
                            </div>
                            {userSubscription?.status === 'cancelled' && userSubscription?.cancelledAt && (
                                <div style={{ marginBottom: '12px' }}>
                                    <strong>Cancelled On:</strong> {formatDate(userSubscription.cancelledAt)}
                                </div>
                            )}
                            {userSubscription?.status === 'active' && (
                                <div style={{ marginBottom: '12px' }}>
                                    <strong>Next Billing Date:</strong> {formatDate(userSubscription?.planEndDate)}
                                </div>
                            )}
                            <div style={{ marginBottom: '12px' }}>
                                <strong>Access Valid Until:</strong>{' '}
                                <span style={{
                                    color: daysRemaining() <= 7 ? '#ef4444' : '#10b981',
                                    fontWeight: '600'
                                }}>
                                    {formatDate(userSubscription?.planEndDate)} ({daysRemaining()} days remaining)
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Cancellation Notice */}
                    {userSubscription?.status === 'cancelled' && (
                        <div style={{
                            backgroundColor: '#fef3c7',
                            border: '1px solid #fbbf24',
                            color: '#92400e',
                            padding: '12px',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            fontSize: '14px'
                        }}>
                            ℹ️ Your subscription is cancelled but you retain full access until {formatDate(userSubscription.planEndDate)}.
                            You will not be charged again unless you reactivate.
                        </div>
                    )}

                    {/* Renewal Notice */}
                    {userSubscription?.status === 'active' && daysRemaining() <= 7 && (
                        <div style={{
                            backgroundColor: '#dcfce7',
                            border: '1px solid #86efac',
                            color: '#166534',
                            padding: '12px',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            fontSize: '14px'
                        }}>
                            📅 Your subscription will automatically renew on {formatDate(userSubscription.planEndDate)} for another month.
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div style={{
                        display: 'flex',
                        gap: '12px',
                        flexDirection: 'column'
                    }}>
                        {/* Cancel/Remove Button - Show if subscription is active */}
                        {userSubscription?.status === 'active' && (
                            <button
                                onClick={handleCancelSubscription}
                                disabled={loading}
                                style={{
                                    padding: '12px 24px',
                                    backgroundColor: 'white',
                                    color: '#dc2626',
                                    border: '1px solid #dc2626',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    opacity: loading ? 0.6 : 1,
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    if (!loading) {
                                        e.target.style.backgroundColor = '#fee2e2';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!loading) {
                                        e.target.style.backgroundColor = 'white';
                                    }
                                }}
                            >
                                {loading ? 'Processing...' : 'Cancel Subscription'}
                            </button>
                        )}

                        {/* Reactivate Button - Show if subscription is cancelled */}
                        {userSubscription?.status === 'cancelled' && daysRemaining() > 0 && (
                            <button
                                onClick={handleReactivateSubscription}
                                disabled={loading}
                                style={{
                                    padding: '12px 24px',
                                    backgroundColor: '#10b981',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    opacity: loading ? 0.6 : 1,
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    if (!loading) e.target.style.backgroundColor = '#059669';
                                }}
                                onMouseLeave={(e) => {
                                    if (!loading) e.target.style.backgroundColor = '#10b981';
                                }}
                            >
                                {loading ? 'Processing...' : 'Reactivate Subscription'}
                            </button>
                        )}

                        {/* Manage Billing Portal Button */}
                        <button
                            onClick={() => window.open('https://billing.stripe.com/p/login/test_28o6ss8HMbh45j28ww', '_blank')}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: '#6366f1',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#4f46e5';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#6366f1';
                            }}
                        >
                            Manage Billing & Payment Methods
                        </button>

                        {/* Contact Support */}
                        <button
                            onClick={() => window.open('mailto:support@ragrecruit.com?subject=Subscription%20Support', '_blank')}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: '#f3f4f6',
                                color: '#1f2937',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#e5e7eb';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#f3f4f6';
                            }}
                        >
                            Contact Support
                        </button>
                    </div>

                    {/* Info Text */}
                    <p style={{
                        marginTop: '20px',
                        fontSize: '12px',
                        color: '#9ca3af',
                        textAlign: 'center'
                    }}>
                        {userSubscription?.status === 'active'
                            ? 'Your subscription will automatically renew each month. Cancel anytime to stop future charges.'
                            : userSubscription?.status === 'cancelled'
                                ? `You have access until ${formatDate(userSubscription?.planEndDate)}. Reactivate anytime to resume monthly billing.`
                                : 'Manage your subscription settings and billing information.'}
                    </p>
                </div>
            </div>
        </>
    );
};

export default SubscriptionModal;